import produce from "immer";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchProjectCategories,
  fetchProjectCategory,
  updateProjectCategory,
  createProjectCategory,
  deleteProjectCategory,
} from "../api/projectCategories";

const initialState = {
  isLoading: false,
  error: false,
  errors: null,
  data: [],
  totalCount: 0,
  currentCount: 0,
  newCount: 0,
};

export const initialController = {
  filters: [],
  page: 0,
  query: "",
  sort: "desc",
  sortBy: "created_at",
  view: "all",
};

export const deleteProjectCategoryAsync = createAsyncThunk(
  "projectCategories/deleteProjectCategoryAsync",
  async (payload) => {
    let response = await deleteProjectCategory(payload);

    // If errors, reject and return response
    if (response.error) return false;

    // If all good, return response.
    return response;
  }
);

export const createProjectCategoryAsync = createAsyncThunk(
  "projectCategories/createProjectCategoryAsync",
  async (payload) => {
    let response = await createProjectCategory(payload);

    // If errors, reject and return response
    if (response.error) return false;

    // If all good, return response.
    return response;
  }
);

export const updateProjectCategoryAsync = createAsyncThunk(
  "projectCategories/updateProjectCategoryAsync",
  async (payload) => {
    let response = await updateProjectCategory(payload);

    // If errors, reject and return response
    if (response.error) return false;

    // If all good, return response.
    return response;
  }
);

export const getProjectCategoriesAsync = createAsyncThunk(
  "projectCategories/getProjectCategoriesAsync",
  async (controller = initialController, { rejectWithValue }) => {
    let response = await fetchProjectCategories(controller);
    // If errors, reject and return response
    if (response.response?.status?.match(/404|500|401/))
      return rejectWithValue(true);

    // If all good, return response.
    return response;
  }
);

export const getProjectCategoryAsync = createAsyncThunk(
  "projectCategories/getProjectCategoryAsync",
  async (id, { rejectWithValue }) => {
    let response = await fetchProjectCategory(id);

    // If errors, reject and return response
    if (response.error) return rejectWithValue(response);

    // If all good, return response.
    return response;
  }
);

const projectCategoriesSlice = createSlice({
  name: "projectCategories",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getProjectCategoriesAsync.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(getProjectCategoriesAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.data;
        state.totalCount = action.payload.totalCount;
        state.currentCount = action.payload.currentCount;
        state.newestProjectCategoriesCount = action.payload.newCount;
      })
      .addCase(getProjectCategoriesAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = [
          "Something went wrong when trying to fetch project categories....is controller missing?",
        ];
      })
      .addCase(updateProjectCategoryAsync.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(updateProjectCategoryAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          const index = draftState.data.findIndex(
            (type) => type.id === action.payload.id
          );
          draftState.data[index] = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(updateProjectCategoryAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = [
          "Something went wrong when trying to update project category",
        ];
      })
      .addCase(createProjectCategoryAsync.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(createProjectCategoryAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.data.push(action.payload);
          draftState.isLoading = false;
        });
      })
      .addCase(createProjectCategoryAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = [
          "Something went wrong when trying to create project category",
        ];
      })
      .addCase(deleteProjectCategoryAsync.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(deleteProjectCategoryAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.data = draftState.data.filter(
            (item) => item.id !== action.meta.arg.id
          );
          draftState.isLoading = false;
        });
      })
      .addCase(deleteProjectCategoryAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = [
          "Something went wrong when trying to delete project category",
        ];
      })
      .addCase(getProjectCategoryAsync.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(getProjectCategoryAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.projectCategory = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(getProjectCategoryAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = [
          "Something went wrong when trying to get project category.",
        ];
      });
  },
});

export const { reducer } = projectCategoriesSlice;

export default projectCategoriesSlice;
